/**
 * Hooks local-first del PLAN DE CLASE / aula invertida (M5).
 *
 * session_module llega por los streams de cohorte (my_cohorts_student/teacher):
 * el alumno ve su plan OFFLINE. Los ticks de completitud se componen reutilizando
 * el progreso que ya existe (lesson_progress, assignment_submission,
 * video_watch_progress). Escritura vía write-path (mutations de @space/shared).
 */
import type { Accessor } from "solid-js";
import {
  type CourseModuleDirectoryRow,
  getAllWatchProgressQuery,
  getClassSessionRowQuery,
  getCourseModuleDirectoryQuery,
  getCourseProgressQuery,
  getMySubmissionsQuery,
  getSessionModulesByCohortQuery,
  getSessionModulesBySessionQuery,
  sessionPlanQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery } from "@space/shared/solid";
import type { ClassSession, SessionModule } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** Ítems del plan (session_module) de una sesión. */
export function useSessionPlanItems(sessionId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<SessionModule>({
    queryKey: () => sessionPlanQueryKeys.bySession(sessionId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = sessionId();
      return db && id ? getSessionModulesBySessionQuery(db, id) : null;
    },
    enabled: () => Boolean(sessionId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Ítems de plan de TODA la cohorte (para marcar "tiene plan" en el horario). */
export function useCohortPlanItems(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<SessionModule>({
    queryKey: () => sessionPlanQueryKeys.byCohort(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getSessionModulesByCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** La fila class_session de una sesión (cabecera del plan). */
export function useSessionRow(sessionId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ClassSession>({
    queryKey: () => sessionPlanQueryKeys.sessionRow(sessionId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = sessionId();
      return db && id ? getClassSessionRowQuery(db, id) : null;
    },
    enabled: () => Boolean(sessionId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Directorio de módulos del curso con título resuelto (para pintar y elegir). */
export function useCourseModuleDirectory(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<CourseModuleDirectoryRow>({
    queryKey: () => sessionPlanQueryKeys.directory(courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = courseId();
      return db && id ? getCourseModuleDirectoryQuery(db, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

interface LessonProgressRow {
  lesson_id: string | null;
  status: string | null;
  /** Nota 0..1 de la lección (fuente `auto_blocks` del boletín del alumno). */
  score: number | null;
}
interface SubmissionRow {
  assignment_id: string | null;
}
interface WatchRow {
  video_id: string | null;
  completed_at: string | null;
}

/** MI progreso de lecciones en un curso (para los ticks del plan). */
export function useMyCourseProgress(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<LessonProgressRow>({
    queryKey: () => sessionPlanQueryKeys.myProgress(base.userId(), courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      const id = courseId();
      return db && uid && id ? getCourseProgressQuery(db, uid, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MIS entregas (para el tick de los módulos assignment del plan). */
export function useMySubmissions() {
  const base = useBase();
  return createPowerSyncQuery<SubmissionRow>({
    queryKey: () => sessionPlanQueryKeys.mySubmissions(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMySubmissionsQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MI progreso de visionado de vídeo (para el tick de los módulos video). */
export function useMyWatchProgress() {
  const base = useBase();
  return createPowerSyncQuery<WatchRow>({
    queryKey: () => sessionPlanQueryKeys.myWatch(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getAllWatchProgressQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
